/**
 * The TypeScript mirror of `points-system-definition.schema.json`.
 *
 * <b>Why this is hand-written and lives here.</b> The API models a definition as an opaque JSON
 * document — it is stored verbatim, must read back byte for byte years later, and nothing queries
 * into it — so the OpenAPI document types it as `JsonElement` and the generated client as
 * `unknown`. There is nothing to generate from. The published JSON Schema is the contract, this
 * file is the shape the designer edits, and `validate.ts` is the client-side reading of the rules
 * the server enforces on write.
 *
 * Keep the three in step: a property added to the schema needs a property here and, if it can be
 * got wrong, a rule there.
 */

export const POINTS_SCHEMA_VERSION = 1;

/**
 * A blank numeric cell, mid-edit.
 *
 * Every `from`, `points` and `value` below is nullable for one reason: a half-typed number is not
 * a number, and binding an input straight to a numeric field turns "clear the box and type 15"
 * into a moment where the value is NaN. Blank is modelled as a state, `validate.ts` refuses it,
 * and the save path is closed while any error stands — so a null never reaches the server.
 *
 * `to` is the exception and its null is real: it means "through the last paid place", which is
 * why only the final row may carry one.
 */
export type Blankable = number | null;

/**
 * Which events get scored (§3.2, §4.2).
 *
 * **Not part of a definition.** A points system answers only "how does a finish become points";
 * which tournaments it is asked that about belongs to the leaderboard using it, which is what
 * makes one system serve every season a room runs. It has its own JSON Schema on the server
 * (`points-scope.schema.json`) and its own validator, and this is the mirror of it.
 *
 * Two things hold one in this package's world: a leaderboard's real scope (§9.9, not built yet),
 * and the preview pane's **test scope** (§9.8), which persists to `PointsSystemVersion.PreviewScope`
 * and scores nothing.
 */
export interface PointsScope {
  /**
   * `yyyy-mm-dd`, inclusive, or **null for no bound on that side**.
   *
   * Guessing at `03/04/2026` books a room's season on the wrong days, so the format is the one the
   * schema states and nothing else. The null is ratings §4.2's, closed on the server at Phase 3b
   * and mirrored here at Phase 6: a rolling-window rating board takes its bounds from its window,
   * and before an open bound was expressible such a board had to carry dates wide enough never to
   * clip it — where a tidy-looking 2020–2030 silently truncates a 36-month window in 2033.
   *
   * <b>`''` is not a member of this type.</b> An empty `<input type="date">` reports one, and
   * `points-scope.schema.json` refuses it against the `yyyy-mm-dd` pattern — so the editors map
   * blank to null on the way out and `readScope` does the same on the way in. §5.2's reference
   * board carries both bounds null, which is what `scope.test.js` gates on: a mirror that cannot
   * hold that document is a builder that refuses the platform's own preset.
   */
  startDate: string | null;
  endDate: string | null;
  /** Empty means no tag requirement. Non-empty means the event must carry at least one. */
  includeTags: string[];
  /** Any one of these vetoes the event. */
  excludeTags: string[];
  /** Overrides every rule here except room ownership. */
  includeEventIds: string[];
  excludeEventIds: string[];
  minFieldSize: number;
}

export interface PointsTableRow {
  from: Blankable;
  /** `null` runs through the last paid place, so only the final row may have one. */
  to: number | null;
  points: Blankable;
}

export interface PointsTableScoring {
  type: 'table';
  /** Ordered; first match wins. A place matching no row scores 0. */
  rows: PointsTableRow[];
}

export interface PointsFormulaScoring {
  type: 'formula';
  expression: string;
}

export type PointsScoring = PointsTableScoring | PointsFormulaScoring;

export interface PointsBand {
  from: Blankable;
  to: number | null;
  value: Blankable;
}

export type FieldMetric = 'entries' | 'uniquePlayers' | 'cashers';
export type BuyInMetric = 'buyIn' | 'prizePool';

export interface PointsMultiplier<TMetric extends string> {
  metric: TMetric;
  /** Clamps the metric *before* the band lookup, so one enormous event cannot dominate. */
  cap: number | null;
  floor: number | null;
  /** A metric matching no band yields 1.0 — defined, but almost never what was meant. */
  bands: PointsBand[];
}

export type FieldMultiplier = PointsMultiplier<FieldMetric>;
export type BuyInMultiplier = PointsMultiplier<BuyInMetric>;

export type ParticipationMode = 'nonCashingEntriesOnly' | 'everyEntry' | 'none';

/** §3.7. Every buy-in the mode admits earns the value, or the tournament earns it once. */
export type ParticipationCount = 'each' | 'oncePerEvent';

/**
 * §3.7. The draft keeps one switch per multiplier; the document says `applyMultipliers: true` or
 * `false` when the two agree, which is every system written before they could be chosen apart,
 * and `{ field, buyIn }` only when they differ (`toDefinitionDocument`).
 */
export interface PointsParticipation {
  mode: ParticipationMode;
  /** The value of one counted entry: a buy-in, or the tournament when `countEntries` is once. */
  pointsPerEntry: Blankable;
  countEntries: ParticipationCount;
  applyFieldMultiplier: boolean;
  applyBuyInMultiplier: boolean;
  countBustedBulletsWhenCashed: boolean;
}

export type DealHandling = 'worstOfDeal' | 'averageOfDeal' | 'actualPlace';
export type ReentryPolicy = 'bestResultOnly' | 'everyEntryScores';

export type PointsAggregation = { mode: 'sumAll' } | { mode: 'bestN'; n: number };

export type Tiebreaker =
  | 'mostFirstPlaces'
  | 'mostCashes'
  | 'bestSingleFinish'
  | 'mostEventsEntered'
  | 'earliestAchieved'
  | 'alphabeticalByLastName';

export const TIEBREAKERS: readonly Tiebreaker[] = [
  'mostFirstPlaces',
  'mostCashes',
  'bestSingleFinish',
  'mostEventsEntered',
  'earliestAchieved',
  'alphabeticalByLastName',
];

export interface PointsDefinition {
  schemaVersion: number;
  name: string;
  description: string | null;
  /**
   * No `scope` (§4.1). The schema closes `additionalProperties`, so a document carrying one is a
   * 400 rather than a silently ignored key — which is the failure that would otherwise matter: a
   * system that reads as covering a fortnight in July while the board scoring with it uses its own
   * dates entirely.
   */
  scoring: PointsScoring;
  fieldMultiplier: FieldMultiplier | null;
  buyInMultiplier: BuyInMultiplier | null;
  participation: PointsParticipation;
  dealHandling: DealHandling;
  reentryPolicy: ReentryPolicy;
  aggregation: PointsAggregation;
  tiebreakers: Tiebreaker[];
  display: { decimalPlaces: number };
  /**
   * schemaVersion 2's three blocks — `bonuses`, `multiplierOverrides` and `tiedPositions`
   * (`docs/specs/points-systems-v2-bonuses.md` §3, §4, §5) — **carried verbatim and never
   * interpreted**.
   *
   * This build has no editor for any of them. What it must not do is lose them: the server accepts
   * a v2 document, so one can exist, and without this the builder would read a system with a
   * bracelet bonus and a field-size bonus, drop both on the floor, and write back a document that
   * still validates — a v2 system with no bonuses is perfectly legal — while every standing it
   * produces silently changes.
   *
   * The same rule the `formula` scoring already follows two paragraphs down in `readDefinition`:
   * something this build cannot edit survives being opened by it. Typed `unknown` deliberately, so
   * nothing here can be tempted to read a field and start making decisions on a shape it does not
   * own; when the designer gains real editors these become proper models and this comment goes.
   */
  bonuses?: unknown;
  multiplierOverrides?: unknown;
  tiedPositions?: unknown;
}

// ---------------------------------------------------------------- reading one back

/**
 * Turns whatever the API handed back into a definition the designer can edit.
 *
 * The server stores a definition verbatim, so a document written by a newer build — or by hand —
 * can carry properties this one has never heard of. Everything recognised is taken as written and
 * everything else is filled from the schema's own defaults, which is what makes an older client
 * open a newer document rather than refuse it.
 *
 * What it must never do is *quietly repair* something meaningful. A `formula` scoring survives as
 * a formula even though this build cannot edit one — the builder says so and blocks saving —
 * because silently rewriting it as an empty table would discard the system.
 */
export function readDefinition(raw: unknown): PointsDefinition {
  const doc = isRecord(raw) ? raw : {};
  const participation = isRecord(doc.participation) ? doc.participation : {};

  return {
    schemaVersion: num(doc.schemaVersion) ?? POINTS_SCHEMA_VERSION,
    name: str(doc.name) ?? '',
    description: str(doc.description) ?? null,
    scoring: readScoring(doc.scoring),
    fieldMultiplier: readMultiplier<FieldMetric>(doc.fieldMultiplier, 'entries', [
      'entries',
      'uniquePlayers',
      'cashers',
    ]),
    buyInMultiplier: readMultiplier<BuyInMetric>(doc.buyInMultiplier, 'buyIn', [
      'buyIn',
      'prizePool',
    ]),
    participation: {
      mode: oneOf(participation.mode, ['nonCashingEntriesOnly', 'everyEntry', 'none'], 'none'),
      pointsPerEntry: num(participation.pointsPerEntry) ?? 1,
      // Absent is every buy-in, which is what every document written before the choice means.
      countEntries: oneOf(participation.countEntries, ['each', 'oncePerEvent'], 'each'),
      ...readParticipationMultipliers(participation.applyMultipliers),
      // Schema default, and the more intuitive reading: every entry a player pays for earns
      // something. The SHRPO reference preset sets it false and says why.
      countBustedBulletsWhenCashed: bool(participation.countBustedBulletsWhenCashed) ?? true,
    },
    dealHandling: oneOf(
      doc.dealHandling,
      ['worstOfDeal', 'averageOfDeal', 'actualPlace'],
      'worstOfDeal',
    ),
    reentryPolicy: oneOf(doc.reentryPolicy, ['bestResultOnly', 'everyEntryScores'], 'bestResultOnly'),
    aggregation: readAggregation(doc.aggregation),
    tiebreakers: strings(doc.tiebreakers).filter((t): t is Tiebreaker =>
      (TIEBREAKERS as readonly string[]).includes(t),
    ),
    display: {
      decimalPlaces: num(isRecord(doc.display) ? doc.display.decimalPlaces : undefined) ?? 2,
    },

    // v2's blocks, taken as written and understood not at all. Spread back out unchanged by
    // `toDefinitionDocument`, so opening a v2 system in this builder and saving it leaves its
    // bonuses exactly as they were rather than deleting them.
    ...(doc.bonuses === undefined ? {} : { bonuses: doc.bonuses }),
    ...(doc.multiplierOverrides === undefined
      ? {}
      : { multiplierOverrides: doc.multiplierOverrides }),
    ...(doc.tiedPositions === undefined ? {} : { tiedPositions: doc.tiedPositions }),
  };
}

function readScoring(raw: unknown): PointsScoring {
  const doc = isRecord(raw) ? raw : {};

  if (doc.type === 'formula') {
    return { type: 'formula', expression: str(doc.expression) ?? '' };
  }

  const rows = Array.isArray(doc.rows) ? doc.rows : [];
  return {
    type: 'table',
    rows: rows.filter(isRecord).map((row) => ({
      from: num(row.from) ?? null,
      to: num(row.to) ?? null,
      points: num(row.points) ?? null,
    })),
  };
}

function readMultiplier<TMetric extends string>(
  raw: unknown,
  fallback: TMetric,
  allowed: readonly TMetric[],
): PointsMultiplier<TMetric> | null {
  // Absent or null is "no weighting at all", which is not the same as a band table that happens
  // to yield 1.0 everywhere — so the distinction survives the round trip.
  if (!isRecord(raw)) return null;

  const bands = Array.isArray(raw.bands) ? raw.bands : [];
  return {
    metric: oneOf(raw.metric, allowed, fallback),
    cap: num(raw.cap) ?? null,
    floor: num(raw.floor) ?? null,
    bands: bands.filter(isRecord).map((band) => ({
      from: num(band.from) ?? null,
      to: num(band.to) ?? null,
      value: num(band.value) ?? null,
    })),
  };
}

function readAggregation(raw: unknown): PointsAggregation {
  if (isRecord(raw) && raw.mode === 'bestN') {
    return { mode: 'bestN', n: Math.max(1, Math.round(num(raw.n) ?? 1)) };
  }
  return { mode: 'sumAll' };
}

/**
 * Reads a stored scope — a version's `previewScope`, or a leaderboard's own.
 *
 * `null` and `undefined` are the ordinary case rather than an error, and they mean different
 * things to the two callers. Most versions have never had a test scope, and `blankScope` is the
 * answer the designer wants there. A leaderboard with none is scored over its own
 * `dateStart`–`dateEnd`, which is a real state and the common one — so §9.9's section keeps the
 * null and says what the fallback is, rather than seeding a document that duplicates the dates.
 * The caller decides; this only reports what was stored.
 */
export function readScope(raw: unknown): PointsScope | null {
  if (!isRecord(raw)) return null;

  return {
    // An absent key, an explicit null and a blank string are one answer — no bound. The first two
    // are one document said two ways (`PointsScope.ToJson` writes every key, a hand-written one
    // omits them); the third cannot be stored, and normalising it here is what keeps a draft that
    // passed through a cleared date box from ever presenting `''` to the schema.
    startDate: blankToNull(str(raw.startDate)),
    endDate: blankToNull(str(raw.endDate)),
    includeTags: strings(raw.includeTags),
    excludeTags: strings(raw.excludeTags),
    includeEventIds: strings(raw.includeEventIds),
    excludeEventIds: strings(raw.excludeEventIds),
    minFieldSize: num(raw.minFieldSize) ?? 0,
  };
}

/**
 * §4.2's date rule: is this `yyyy-mm-dd` inside the scope's bounds, either of which may be open?
 *
 * <b>One implementation, because there are two browser-side readers and they must not be able to
 * disagree</b> — §9.9's leaderboard candidate list and §9.7's empty-scope diagnosis. It mirrors
 * `PointsEngine.ResolveScope`, which writes the same rule out explicitly rather than leaving it to
 * a language default, and for the same reason:
 *
 * <b>one null, two languages, opposite defaults.</b> In C# a lifted `date &lt; null` is *false* and
 * therefore admits; in SQL `[Date] &gt;= @start` against NULL is *UNKNOWN* and therefore admits
 * nothing. JavaScript gives the SQL answer in the C# syntax — `'2026-01-01' &gt;= null` coerces the
 * null to 0 and compares a string against a number, which is `false` — so the obvious expression
 * reads as "no lower bound, so everything is in" and drops **every** event. Against §5.2's
 * reference board, whose bounds are both null, that is a screen telling an operator that the
 * platform's own preset matches no tournament in their room.
 *
 * The De Morgan rewrite that looks identical (`!(d &gt;= from &amp;&amp; d &lt;= to)`) has the same defect and
 * is the form to refuse in review. Both edges are inclusive.
 */
export function withinScopeDates(date: string, scope: PointsScope): boolean {
  if (scope.startDate !== null && date < scope.startDate) return false;
  if (scope.endDate !== null && date > scope.endDate) return false;
  return true;
}

/** Blank is not a date; it is the absence of one. See `PointsScope.startDate`. */
function blankToNull(value: string | null): string | null {
  return value === null || value.length === 0 ? null : value;
}

/**
 * The scope document to send, trimmed the way `toDefinitionDocument` trims a definition: the
 * schema closes `additionalProperties`, so only these seven keys may travel.
 *
 * Both bounds are written **explicitly**, null included, rather than omitted when open. The schema
 * accepts either — they are one document said two ways — and writing the key is what makes the
 * round trip byte-comparable against what the server stored, which is how `scope.test.js` gates
 * this module.
 */
export function toScopeDocument(scope: PointsScope): unknown {
  return {
    startDate: scope.startDate,
    endDate: scope.endDate,
    includeTags: scope.includeTags,
    excludeTags: scope.excludeTags,
    includeEventIds: scope.includeEventIds,
    excludeEventIds: scope.excludeEventIds,
    minFieldSize: scope.minFieldSize,
  };
}

/** A stable string for one scope, for keying a cached preview. See `definitionKey`. */
export function scopeKey(scope: PointsScope): string {
  return JSON.stringify(toScopeDocument(scope));
}

// ---------------------------------------------------------------- writing one out

/**
 * The document to send.
 *
 * Several branches of the schema close `additionalProperties`, so a field left over from the
 * other branch is a hard rejection rather than an ignored extra: `aggregation` must not carry `n`
 * when the mode is `sumAll`, and a table scoring must not carry an `expression`. They are
 * stripped here rather than in the editors, so a component can keep whatever it needs to switch
 * back and forth without the operator losing what they typed.
 *
 * The **top level** closes it too, which is what enforces §4.1: there is no `scope` on
 * `PointsDefinition`, so none can be spread into the document here, and a stray one would be
 * refused rather than ignored.
 *
 * Participation is the subtler one. With mode `none` the entry figures mean nothing, and the
 * schema types `pointsPerEntry` as a plain number — so a box left blank while the mode was
 * something else, and then switched to `none`, would send a `null` the server refuses. Since
 * `none` is exactly "no points for an entry", the document simply says that and no more.
 */
/**
 * §3.7. A boolean is both or neither — the only form there was before the two could be chosen —
 * and an object names each. Absent reads as both, the designer's own default for a new system.
 */
function readParticipationMultipliers(
  raw: unknown,
): Pick<PointsParticipation, 'applyFieldMultiplier' | 'applyBuyInMultiplier'> {
  if (isRecord(raw)) {
    return {
      applyFieldMultiplier: bool(raw.field) ?? false,
      applyBuyInMultiplier: bool(raw.buyIn) ?? false,
    };
  }

  const both = bool(raw) ?? true;
  return { applyFieldMultiplier: both, applyBuyInMultiplier: both };
}

/**
 * §3.7. The participation block as the schema takes it, written so that a system using neither of
 * the newer choices goes out exactly as it did before them: the boolean `applyMultipliers` when
 * the two switches agree, and no `countEntries` while it is the default. A published definition is
 * stored verbatim, so a document that changed for nothing on every save would be a new version's
 * worth of noise.
 */
function participationDocument(participation: PointsParticipation): unknown {
  if (participation.mode === 'none') return { mode: 'none' };

  const { applyFieldMultiplier: field, applyBuyInMultiplier: buyIn } = participation;

  return {
    mode: participation.mode,
    pointsPerEntry: participation.pointsPerEntry,
    applyMultipliers: field === buyIn ? field : { field, buyIn },
    countBustedBulletsWhenCashed: participation.countBustedBulletsWhenCashed,
    ...(participation.countEntries === 'oncePerEvent' ? { countEntries: 'oncePerEvent' } : {}),
  };
}

export function toDefinitionDocument(definition: PointsDefinition): unknown {
  // The spread is what carries v2's `bonuses`, `multiplierOverrides` and `tiedPositions` back out
  // untouched — they are on the definition only because `readDefinition` put them there, and
  // nothing between here and the server looks inside them. `JSON.stringify` drops any that are
  // absent, so a v1 document still goes out as a v1 document.
  return {
    ...definition,
    scoring:
      definition.scoring.type === 'table'
        ? { type: 'table', rows: definition.scoring.rows }
        : { type: 'formula', expression: definition.scoring.expression },
    participation: participationDocument(definition.participation),
    aggregation:
      definition.aggregation.mode === 'bestN'
        ? { mode: 'bestN', n: definition.aggregation.n }
        : { mode: 'sumAll' },
  };
}

/**
 * A stable string for one definition, for comparing drafts and for keying a cached preview.
 *
 * `JSON.stringify` of an object literal is stable here because every one of these objects is
 * built by this module in a fixed property order — it is not a general-purpose canonicaliser,
 * and it does not need to be.
 */
export function definitionKey(definition: PointsDefinition): string {
  return JSON.stringify(toDefinitionDocument(definition));
}

// ---------------------------------------------------------------- reading the numbers

/**
 * What a place is awarded before any multiplier. First match wins, and a place matching no row
 * scores 0 — a real answer, not a gap.
 */
export function awardForPlace(rows: readonly PointsTableRow[], place: number): number {
  for (const row of rows) {
    if (row.from === null || place < row.from) continue;
    if (row.to !== null && place > row.to) continue;
    return row.points ?? 0;
  }
  return 0;
}

/** The multiplier one metric value lands on. Anything matching no band is 1.0. */
export function multiplierFor(
  multiplier: PointsMultiplier<string> | null,
  metricValue: number,
): number {
  if (!multiplier) return 1;

  // Clamped before the lookup, per §3.6 — that is the whole point of a cap.
  let value = metricValue;
  if (multiplier.floor !== null) value = Math.max(value, multiplier.floor);
  if (multiplier.cap !== null) value = Math.min(value, multiplier.cap);

  for (const band of multiplier.bands) {
    if (band.from === null || value < band.from) continue;
    if (band.to !== null && value > band.to) continue;
    return band.value ?? 1;
  }
  return 1;
}

/**
 * How many places a chart of this table should draw.
 *
 * The last row usually runs to the last paid place, which no table knows, so the chart shows the
 * shape up to a little past where the table stops discriminating — enough to read the tail
 * without pretending to know how deep the money went.
 */
export function chartPlaceCount(rows: readonly PointsTableRow[]): number {
  const last = rows[rows.length - 1];
  if (!last) return 0;

  const explicit = last.to ?? (last.from ?? 0) + 8;
  return Math.min(Math.max(explicit, 9), 64);
}

// ---------------------------------------------------------------- narrow readers

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function bool(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}
